import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router";
import {
  Autocomplete,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  LongTextLabel,
  useLocalStorage,
} from "@sscs/ui";
import { ChevronLeftIcon } from "@sscs/ui/icons";
import { compareValues, getClassName } from "@sscs/utils";
import { Dialog } from "Components";
import { FILESTREAM_ROUTE, REPORTS_ROUTE } from "Constants";
import { useAppDispatch, useGroup } from "Hooks";
import { useGetWhoamiQuery } from "Services";
import { removeReportsThunk, selectReports } from "Store/reports.slice";
import { WhoamiGroup } from "Types";
import styles from "./orgAndGroupSelect.module.scss";

const HIDE_SWITCH_GROUP_DIALOG_KEY = "hideSwitchGroupDialog";

type Props = {
  backToGroup: { name: string; path: string } | null;
};

const OrgAndGroupSelect = ({ backToGroup }: Props): React.JSX.Element => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: whoami } = useGetWhoamiQuery();
  const { reports } = useSelector(selectReports);
  const currentGroup = useGroup();
  const [pendingSwitch, setPendingSwitch] = useState<{
    group: WhoamiGroup;
    dontShowAgain: boolean;
  } | null>(null);
  const [hideDialogPreference, setHideDialogPreference] = useLocalStorage(
    HIDE_SWITCH_GROUP_DIALOG_KEY,
  );
  const sortedGroups =
    whoami?.org_groups.toSorted((first, second) =>
      compareValues(first.name, second.name, false),
    ) ?? [];

  const openReportKeys = Object.keys(reports);

  const switchGroup = (group: WhoamiGroup): void => {
    dispatch(removeReportsThunk(openReportKeys));

    const portalPage = pathname.split("/")[1];
    const landingPage =
      portalPage === REPORTS_ROUTE ? FILESTREAM_ROUTE : portalPage;

    navigate(`/${landingPage}/group/${group.group_id}`);
  };

  const handleGroupSelect = (group: WhoamiGroup): void => {
    if (openReportKeys.length > 0 && hideDialogPreference !== "true") {
      setPendingSwitch({ group, dontShowAgain: false });
    } else {
      switchGroup(group);
    }
  };

  const closeDialog = (): void => setPendingSwitch(null);

  return (
    <>
      {whoami && (
        <nav aria-label="Organization and group" className={styles.breadcrumb}>
          {/* Users belong to exactly one organization, so the org is a static
          crumb; only the group is switchable, via the trailing dropdown. */}
          <div className={styles.organization}>
            <span className={styles["organization-label"]}>Organization</span>
            <LongTextLabel
              className={styles["organization-name"]}
              label={whoami.organization_name}
            />
          </div>
          <span
            aria-hidden
            className={getClassName(styles.separator, {
              [styles["separator-hidden"]]: !currentGroup,
            })}
          >
            /
          </span>
          {currentGroup ? (
            <Autocomplete
              id="group"
              className={styles["group-select"]}
              label="Group"
              disableClearable
              options={sortedGroups}
              value={currentGroup}
              getOptionLabel={(group) => group.name}
              isOptionEqualToValue={(option, selected) =>
                option.group_id === selected.group_id
              }
              onChange={(_, group) => handleGroupSelect(group)}
              inputProps={{ "aria-label": "Group", title: currentGroup.name }}
            />
          ) : (
            backToGroup && (
              <ButtonBase
                id="back-to-group"
                className={styles["back-button"]}
                component={Link}
                to={backToGroup.path}
              >
                <ChevronLeftIcon />
                Back to group:&nbsp;
                <b>
                  <LongTextLabel label={backToGroup.name} />
                </b>
              </ButtonBase>
            )
          )}
        </nav>
      )}
      <Dialog
        isOpen={Boolean(pendingSwitch)}
        title="Switch group?"
        onConfirm={() => {
          if (!pendingSwitch) {
            return;
          }

          if (pendingSwitch.dontShowAgain) {
            setHideDialogPreference("true");
          }

          switchGroup(pendingSwitch.group);
          closeDialog();
        }}
        onClose={closeDialog}
        onCancel={closeDialog}
        confirmText="Switch group"
        meta={
          <FormControlLabel
            label="Don't show this again"
            control={
              <Checkbox
                size="small"
                checked={pendingSwitch?.dontShowAgain ?? false}
                onChange={(_, checked) =>
                  setPendingSwitch(
                    (current) =>
                      current && { ...current, dontShowAgain: checked },
                  )
                }
              />
            }
          />
        }
      >
        <p>
          Switching to <b>{pendingSwitch?.group.name}</b> group closes your{" "}
          {openReportKeys.length} open report
          {openReportKeys.length === 1 ? "" : "s"}.
        </p>
        <p>A report belongs to a group, so it cannot move with you.</p>
      </Dialog>
    </>
  );
};

export default OrgAndGroupSelect;
